/**
 * Accordion — a minimal compound component for expand/collapse content.
 *
 * Library responsibilities (deliberately small):
 *  1. Disclosure state management (single, multi-expand, controlled)
 *      via react-aria-components.
 *  2. The smooth grid-template-rows expand/collapse animation. This is
 *      the one consistency the design system enforces.
 *  3. Correct a11y wiring — `aria-expanded`, `aria-controls`, focus
 *      management, and `inert` for collapsed content.
 *
 * Consumer responsibilities (deliberately broad):
 *  - What the trigger row LOOKS like — padding, hover/active states,
 *    typography, layout, leading/trailing icons or actions.
 *  - WHAT renders inside the trigger and panel based on `isExpanded`
 *    (e.g. swap a snippet for a full body when expanded).
 *  - Whether the chevron is on the left, on the right, swap-icon style,
 *    or absent entirely.
 *  - Whether to wrap the entire row in `<AccordionTrigger>` (clicking
 *    anywhere toggles, no nested interactive elements allowed inside)
 *    or use `<AccordionTrigger>` for just the chevron and let the rest
 *    of the row stay non-clickable / hold its own buttons and links.
 *
 * The four exported components are:
 *   <Accordion>          DisclosureGroup wrapper. Manages multi-expand.
 *   <AccordionItem>      Single disclosure. Children render with access
 *                        to `{ isExpanded }` when passed as a function.
 *   <AccordionTrigger>   The toggle button. Children default to a
 *                        rotating ChevronDown; pass any ReactNode to
 *                        override (icon, text, or whole header row).
 *   <AccordionPanel>     Animated content reveal. Reads expanded state
 *                        from react-aria's DisclosureStateContext for
 *                        inert handling.
 */

import { ChevronDown, ChevronRight } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import type {
  ButtonProps as AriaButtonProps,
  DisclosureGroupProps as AriaDisclosureGroupProps,
  DisclosurePanelProps as AriaDisclosurePanelProps,
  DisclosureProps as AriaDisclosureProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosureGroup as AriaDisclosureGroup,
  DisclosurePanel as AriaDisclosurePanel,
  DisclosureStateContext,
} from 'react-aria-components';

import { cx } from '@/utils/cx';

// ---------------------------------------------------------------------------
// Accordion (DisclosureGroup wrapper)
// ---------------------------------------------------------------------------

export interface AccordionProps extends AriaDisclosureGroupProps {
  children: ReactNode;
}

export const Accordion = ({
  children,
  className,
  ...props
}: AccordionProps) => (
  <AriaDisclosureGroup
    {...props}
    className={values =>
      cx(
        'flex flex-col',
        typeof className === 'function' ? className(values) : className
      )
    }
  >
    {children}
  </AriaDisclosureGroup>
);

// ---------------------------------------------------------------------------
// AccordionItem (Disclosure wrapper). Children compose the trigger + panel.
// Pass a render-prop `({ isExpanded }) => …` for state-aware composition.
// ---------------------------------------------------------------------------

export interface AccordionItemRenderProps {
  isExpanded: boolean;
}

export type AccordionItemChildren =
  | ReactNode
  | ((state: AccordionItemRenderProps) => ReactNode);

export interface AccordionItemProps extends Omit<
  AriaDisclosureProps,
  'children' | 'className'
> {
  children: AccordionItemChildren;
  className?: string;
}

export const AccordionItem = ({
  children,
  className,
  ...props
}: AccordionItemProps) => (
  <AriaDisclosure
    {...props}
    className={values =>
      cx(
        // `group/item` exposes `data-expanded` to descendants via
        // Tailwind's `group-data-[expanded]/item:` selector. The default
        // chevron rotation in <AccordionTrigger> and the row-grow
        // transition in <AccordionPanel> both read this — pure CSS,
        // no prop plumbing required for the common case.
        'group/item',
        values.isDisabled && 'opacity-50',
        className
      )
    }
  >
    {typeof children === 'function'
      ? renderProps => children({ isExpanded: renderProps.isExpanded })
      : children}
  </AriaDisclosure>
);

// ---------------------------------------------------------------------------
// AccordionTrigger — the toggle button.
//
// Children default to a rotating ChevronDown. Consumer composes anything
// they want as children: a single chevron, a swap-icon pair, or an entire
// header row (so long as the row contains no nested interactive elements
// — that's an HTML rule, not one we can override).
// ---------------------------------------------------------------------------

export interface AccordionTriggerProps extends Omit<
  AriaButtonProps,
  'slot' | 'children' | 'className'
> {
  children?: ReactNode;
  className?: string;
}

export const AccordionTrigger = ({
  children,
  className,
  ...props
}: AccordionTriggerProps) => (
  <AriaButton
    slot='trigger'
    {...props}
    className={cx(
      // The only library concerns: focus-visible (a11y), cursor +
      // disabled hint (interaction). Everything else — padding, hover
      // bg, typography, layout — is the consumer's choice.
      'cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] disabled:cursor-not-allowed',
      className
    )}
  >
    {/* `undefined` children → default <AccordionChevron> + sr-only
        label (common case for "chevron-as-trigger"). The chevron is
        `aria-hidden`, so an accessible name has to come from somewhere;
        the sr-only span is the fallback when no consumer-supplied text
        exists. Explicit `null` children → render an invisible button
        (overlay trigger pattern, where the visible chevron + label
        live in a sibling layer). Any other ReactNode → render as-is
        (consumer is responsible for the accessible name in that
        case). */}
    {children === undefined ? (
      <>
        <span className='sr-only'>Toggle section</span>
        <AccordionChevron />
      </>
    ) : (
      children
    )}
  </AriaButton>
);

// ---------------------------------------------------------------------------
// AccordionChevron — the four chevron rotation patterns the design system
// uses with <AccordionItem>, in one component. Rotation is driven by the
// chevron's OWN <AccordionItem>'s state via react-aria's
// `DisclosureStateContext` when one is present — this is what keeps nested
// accordions independent (the nearest-provider context always resolves to
// the innermost Disclosure, unlike the CSS `group-data-[expanded]/item`
// selector, which matches the nearest ANCESTOR `group/item` and would
// otherwise pick up an expanded parent's state). When no
// DisclosureStateContext is present (e.g. ActionOverview, which stamps
// `group/item` + `data-expanded` manually without a react-aria Disclosure),
// falls back to the CSS `group-data` selector.
//
// Variants:
//   default   — points down, rotates 180° on expand. Standard chevron.
//   inverted  — points up by default, rotates to down on expand. No
//               current consumers (formerly used by the slideout /
//               meeting-prep sections); retained as design-system API.
//   leading   — points right by default (-90°), rotates to down on
//               expand. Used when the chevron sits on the LEFT of the
//               trigger row (CSV-import CategorySection / RowItem).
//   swap      — renders ChevronRight collapsed and ChevronDown expanded
//               (two icons hidden/shown via `group-data-[expanded]`).
//               Used by surfaces that prefer a sharper "right → down"
//               affordance over a smooth rotation (CSV-import
//               MappingStep, WebhooksApiKeysSection).
// ---------------------------------------------------------------------------

export type AccordionChevronVariant =
  | 'default'
  | 'inverted'
  | 'leading'
  | 'swap';

export interface AccordionChevronProps {
  variant?: AccordionChevronVariant;
  className?: string;
  /** Forwarded to the underlying icon. Defaults to the icon set's default
   *  (~2). Some surfaces use 1.5 for a lighter weight (ToolUseItem,
   *  ReasoningStepsList). */
  strokeWidth?: number;
}

// Own-state rotation (used when a DisclosureStateContext is available).
// [collapsed, expanded]
const ROTATION_BY_STATE: Record<
  Exclude<AccordionChevronVariant, 'swap'>,
  readonly [string, string]
> = {
  default: ['rotate-0', 'rotate-180'],
  inverted: ['rotate-180', 'rotate-0'],
  leading: ['-rotate-90', 'rotate-0'],
};

// CSS-selector fallback (used when no DisclosureStateContext is present).
//
// Every value below must be a complete, unbroken Tailwind class literal
// written directly in this file. Tailwind's content scanner does static
// text extraction over source files — it cannot resolve JS template
// interpolation (e.g. `` `group-data-[expanded]/item:${x}` ``), so a class
// name assembled that way would silently fail to be generated in the built
// CSS, with no build error and no test failure. The
// `Record<keyof typeof ROTATION_BY_STATE, string>` type keeps this map's
// keys from drifting out of sync with ROTATION_BY_STATE — TypeScript
// errors if a variant is added to one but not the other. Each entry
// mirrors ROTATION_BY_STATE by hand: the collapsed class is included only
// when it isn't the neutral `rotate-0` (the CSS default), and the expanded
// class is gated behind the `group-data-[expanded]/item:` selector.
const ROTATION_CLASS: Record<keyof typeof ROTATION_BY_STATE, string> = {
  default: 'group-data-[expanded]/item:rotate-180',
  inverted: 'rotate-180 group-data-[expanded]/item:rotate-0',
  leading: '-rotate-90 group-data-[expanded]/item:rotate-0',
};

const CHEVRON_BASE_CLASSES =
  'size-4 shrink-0 text-[var(--color-text-secondary)] transition-transform duration-200 motion-reduce:transition-none';

export const AccordionChevron = ({
  variant = 'default',
  className,
  strokeWidth,
}: AccordionChevronProps) => {
  const state = useContext(DisclosureStateContext);
  const hasDisclosureContext = state != null;
  const isExpanded = state?.isExpanded ?? false;

  if (variant === 'swap') {
    if (hasDisclosureContext) {
      const Icon = isExpanded ? ChevronDown : ChevronRight;
      return (
        <Icon
          aria-hidden='true'
          strokeWidth={strokeWidth}
          data-testid='accordion-chevron'
          className={cx(
            'size-4 shrink-0 text-[var(--color-text-secondary)]',
            className
          )}
        />
      );
    }
    // Fallback: no Disclosure context — keep the existing two-icon hide/show
    // via group-data (unchanged from today).
    return (
      <>
        <ChevronRight
          aria-hidden='true'
          strokeWidth={strokeWidth}
          data-testid='accordion-chevron-collapsed'
          className={cx(
            'size-4 shrink-0 text-[var(--color-text-secondary)] group-data-[expanded]/item:hidden',
            className
          )}
        />
        <ChevronDown
          aria-hidden='true'
          strokeWidth={strokeWidth}
          data-testid='accordion-chevron-expanded'
          className={cx(
            'hidden size-4 shrink-0 text-[var(--color-text-secondary)] group-data-[expanded]/item:inline',
            className
          )}
        />
      </>
    );
  }
  const rotationClass = hasDisclosureContext
    ? ROTATION_BY_STATE[variant][isExpanded ? 1 : 0]
    : ROTATION_CLASS[variant];
  return (
    <ChevronDown
      aria-hidden='true'
      strokeWidth={strokeWidth}
      data-testid='accordion-chevron'
      className={cx(CHEVRON_BASE_CLASSES, rotationClass, className)}
    />
  );
};

// ---------------------------------------------------------------------------
// AccordionPanel — animated content reveal.
//
// Reads `isExpanded` from react-aria's DisclosureStateContext so consumers
// don't have to plumb it through render-props just to mark collapsed
// content `inert`.
// ---------------------------------------------------------------------------

export interface AccordionPanelProps extends Omit<
  AriaDisclosurePanelProps,
  'className'
> {
  children: ReactNode;
  /** Class names applied to the panel's inner content wrapper. Padding
   *  applied here only takes height when the panel is expanded — it's
   *  clipped by `overflow-hidden` when collapsed. */
  className?: string;
}

export const AccordionPanel = ({
  children,
  className,
  ...props
}: AccordionPanelProps) => {
  const state = useContext(DisclosureStateContext);
  const isExpanded = state?.isExpanded ?? false;
  return (
    <AriaDisclosurePanel
      {...props}
      // Mark collapsed content inert so it isn't reachable via keyboard /
      // screen reader. Without this our `[&[hidden]]:!grid` override
      // (below) would leave the collapsed children focusable, even
      // though they look hidden.
      inert={isExpanded ? undefined : true}
      // Pure-CSS expand/collapse via grid-template-rows interpolation
      // (0fr → 1fr). Cheaper than animating height; no layout thrash
      // when many items are on screen.
      //
      // - `data-expanded` is set by react-aria on the parent
      //   <AriaDisclosure> (our <AccordionItem>), which has
      //   `group/item`. We read it via `group-data-[expanded]/item:`.
      // - `min-h-0` on the inner wrapper lets the row collapse below
      //   intrinsic content height (required for the grid-rows
      //   transition).
      // - `[&[hidden]]:!grid` overrides react-aria's `[hidden]`
      //   attribute (which would otherwise force display:none and
      //   skip the transition). `inert` (above) preserves a11y
      //   semantics for the collapsed state.
      className='grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none group-data-[expanded]/item:grid-rows-[1fr] [&[hidden]]:!grid [&[hidden]]:!grid-rows-[0fr]'
    >
      <div className='min-h-0 overflow-hidden'>
        {/* Consumer's className goes on the INNER content wrapper, not
            the outer grid container. Putting it on the grid container
            would let padding (e.g. `pb-4`) leak out as empty space when
            collapsed (the grid row is 0fr but the container's own
            padding still takes height). On the inner wrapper, padding
            is clipped by `overflow-hidden` when collapsed and only
            shows once the panel grows open. */}
        <div className={className}>{children}</div>
      </div>
    </AriaDisclosurePanel>
  );
};
